import { Routes, Route, Navigate } from 'react-router-dom'
import { AppProviders } from '@kolab/dms-base/providers'
import { ProtectedRoute } from '@kolab/dms-base/components/guards'
import { Shell } from '@kolab/dms-base/components/layout'
import { LoginPage, ForbiddenPage, ChangePasswordPage, ProfilePage } from '@kolab/dms-base/features/auth'
import { UsersPage, RolesPage, TenantsPage } from '@kolab/dms-base/features/iam'
import { menuConfig } from './config/menu'
import ErrorBoundary from './components/common/ErrorBoundary'
import { PermissionRoute, P } from './rbac'
import Dashboard from './pages/Dashboard'
import DistributorsPage from './pages/Distributors'
import ResellersPage from './pages/Resellers'
import LicenseProductsPage from './pages/LicenseProducts'
import DistributorLicensesPage from './pages/DistributorLicenses'
import ResellerLicensesPage from './pages/ResellerLicenses'
import DeviceLicensesPage from './pages/DeviceLicenses'
import PoolAvailabilityReport from './pages/reports/PoolAvailabilityReport'
import ResellerCapacityReport from './pages/reports/ResellerCapacityReport'
import ExpiringDevicesReport from './pages/reports/ExpiringDevicesReport'
import DeviceInstallationReport from './pages/reports/DeviceInstallationReport'
import DeviceLockUnlockReport from './pages/reports/DeviceLockUnlockReport'
import DeviceListPage from './pages/devices/DeviceListPage'
import DeviceDetailPage from './pages/devices/DeviceDetailPage'
import PingHistoryPage from './pages/devices/PingHistoryPage'
import CommandHistoryPage from './pages/devices/CommandHistoryPage'
import NotFound from './pages/NotFound'

const iamUserPerms = {
  createPermission: P.identity_and_access.user.create,
  editPermission: P.identity_and_access.user.update,
  deletePermission: P.identity_and_access.user.delete,
  resetPasswordPermission: P.identity_and_access.user.reset_password,
} as const

const iamRolePerms = {
  createPermission: P.identity_and_access.role.create,
  editPermission: P.identity_and_access.role.update,
} as const

const iamTenantPerms = {
  createPermission: P.identity_and_access.tenant.create,
} as const

function App() {
  return (
    <AppProviders>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/403" element={<ForbiddenPage />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Shell menuConfig={menuConfig}>
                {/*
                  Page-scoped ErrorBoundary lives INSIDE the shell so a page
                  crash keeps the sidebar/topbar usable. See ErrorBoundary —
                  it resets when the URL changes.
                */}
                <ErrorBoundary>
                  <Routes>
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    <Route
                      path="dashboard"
                      element={
                        <PermissionRoute
                          permission={[
                            P.dashboard.device_stats_card.view,
                            P.dashboard.pool_availability_card.view,
                            P.dashboard.upcoming_expirations_card.view,
                          ]}
                        >
                          <Dashboard />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="distributors"
                      element={
                        <PermissionRoute permission={P.tenants.distributor_list.view}>
                          <DistributorsPage />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="resellers"
                      element={
                        <PermissionRoute permission={P.tenants.reseller_list.view}>
                          <ResellersPage />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="license-products"
                      element={
                        <PermissionRoute permission={P.license_catalog.product_list.view}>
                          <LicenseProductsPage />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="licenses/distributor"
                      element={
                        <PermissionRoute permission={P.licenses.distributor_pool_list.view}>
                          <DistributorLicensesPage />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="licenses/reseller"
                      element={
                        <PermissionRoute permission={P.licenses.reseller_allocation_list.view}>
                          <ResellerLicensesPage />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="devices"
                      element={
                        <PermissionRoute permission={P.device_licenses.list.view}>
                          <DeviceLicensesPage />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="device-ops"
                      element={
                        <PermissionRoute permission={P.device_operations.device_list.view}>
                          <DeviceListPage />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="device-ops/:deviceId"
                      element={
                        <PermissionRoute permission={P.device_operations.device_detail.view}>
                          <DeviceDetailPage />
                        </PermissionRoute>
                      }
                    />
                    <Route path="device-ops/dashboard" element={<Navigate to="/dashboard" replace />} />
                    <Route
                      path="pings"
                      element={
                        <PermissionRoute permission={P.device_operations.ping_history.view}>
                          <PingHistoryPage />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="commands"
                      element={
                        <PermissionRoute permission={P.device_operations.command_history.view}>
                          <CommandHistoryPage />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="reports/pool-availability"
                      element={
                        <PermissionRoute permission={P.reports.pool_availability.view}>
                          <PoolAvailabilityReport />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="reports/reseller-capacity"
                      element={
                        <PermissionRoute permission={P.reports.reseller_capacity.view}>
                          <ResellerCapacityReport />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="reports/expiring-devices"
                      element={
                        <PermissionRoute permission={P.reports.expiring_devices.view}>
                          <ExpiringDevicesReport />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="reports/device-installation"
                      element={
                        <PermissionRoute permission={P.reports.device_installation.view}>
                          <DeviceInstallationReport />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="reports/device-lock-unlock"
                      element={
                        <PermissionRoute permission={P.reports.device_lock_unlock.view}>
                          <DeviceLockUnlockReport />
                        </PermissionRoute>
                      }
                    />
                    {/*
                      IAM UsersPage handles create / detail / edit modes based
                      on the URL segment; we gate on user_list.view for the
                      landing and let the page component enforce finer
                      permissions on its own action buttons.
                    */}
                    <Route
                      path="iam/users"
                      element={
                        <PermissionRoute permission={P.identity_and_access.user_list.view}>
                          <UsersPage {...iamUserPerms} />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="iam/users/:id"
                      element={
                        <PermissionRoute permission={P.identity_and_access.user_detail.view}>
                          <UsersPage {...iamUserPerms} />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="iam/users/:id/edit"
                      element={
                        <PermissionRoute permission={P.identity_and_access.user.update}>
                          <UsersPage {...iamUserPerms} />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="iam/users/create"
                      element={
                        <PermissionRoute permission={P.identity_and_access.user.create}>
                          <UsersPage {...iamUserPerms} />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="iam/roles"
                      element={
                        <PermissionRoute permission={P.identity_and_access.role_list.view}>
                          <RolesPage {...iamRolePerms} />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="iam/roles/:id"
                      element={
                        <PermissionRoute permission={P.identity_and_access.role_detail.view}>
                          <RolesPage {...iamRolePerms} />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="iam/roles/:id/edit"
                      element={
                        <PermissionRoute permission={P.identity_and_access.role.update}>
                          <RolesPage {...iamRolePerms} />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="iam/roles/create"
                      element={
                        <PermissionRoute permission={P.identity_and_access.role.create}>
                          <RolesPage {...iamRolePerms} />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="iam/tenants"
                      element={
                        <PermissionRoute permission={P.identity_and_access.tenant_list.view}>
                          <TenantsPage {...iamTenantPerms} />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="iam/tenants/:id"
                      element={
                        <PermissionRoute permission={P.identity_and_access.tenant_detail.view}>
                          <TenantsPage {...iamTenantPerms} />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="iam/tenants/:id/edit"
                      element={
                        <PermissionRoute permission={P.identity_and_access.tenant_detail.view}>
                          <TenantsPage {...iamTenantPerms} />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="iam/tenants/create"
                      element={
                        <PermissionRoute permission={P.identity_and_access.tenant.create}>
                          <TenantsPage {...iamTenantPerms} />
                        </PermissionRoute>
                      }
                    />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </ErrorBoundary>
              </Shell>
            </ProtectedRoute>
          }
        />
      </Routes>
    </AppProviders>
  )
}

export default App
